/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Timeline from "./Timeline";
import WorkflowLogs from "./WorkflowLogs";
import ProjectInfo from "./ProjectInfo";
import { proxyAPI } from "../../proxyAPI";
import ContactsIcon from "../../../assets/icons/contact_edit.svg2";
import EditIcon from "../../../assets/icons/edit-01.svg2";
import ProjectIcon from "../../../assets/icons/Icon_Library/application icons/reports/process_reports.svg2";
import StopIcon from "../../../assets/icons/Icon_Library/application icons/assets/events/ticker_pause-01.svg2";
import FullScreenLoadingIndicator from "../../Common/FullScreenLoadingIndicator";
import ReportingLinks from "../../Reporting/ReportingLinks";
import WhiteButton from "../../Common/WhiteButton";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { faRectangleList } from "@fortawesome/free-regular-svg-icons";

const { baseUrl: baseURL } = JSON.parse(
  document.querySelector("#app").dataset.page
).props;

export default function Process() {
  const [project, setProject] = useState(null);
  const [process, setProcess] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showLogs, setShowLogs] = useState({ GUID: null, runtimeID: null });
  const { processID } = useParams();
  const [refresh, setRefresh] = useState(new Date());

  const [serachParams, setSP] = useSearchParams();

  const loader = async (
    startAt,
    endAt,
    status,
    offset,
    limit = Number(serachParams.get("limit")) || 10
  ) => {
    setLoading(true);
    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: `/process?id=${processID}&start_at=${startAt.toISOString()}&end_at=${endAt.toISOString()}&offset=${offset}&status=${status}&limit=${limit}`,
    });
    setTimeout(() => {
      if (data.process) {
        setTotal(data.total);
        setProcesses(data.runtimes);
        setProcess(data.process);
        setProject(data.process.Project);
      }
      setLoading(false);
    }, 500);
  };

  const stopProcess = async (id) => {
    setLoading(true);
    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: `/stop-process/${id}`,
    });
    if (data) {
      setRefresh(new Date());
    }

    setLoading(false);
  };

  useEffect(() => {
    if (process?.StartAt) {
      setSP((prev) => {
        const newParams = new URLSearchParams(prev);
        if (!newParams.has("start_at") && !newParams.has("zoom")) {
          const startAt = new Date(process.StartAt);
          startAt.setMinutes(startAt.getMinutes() - 10);
          newParams.set("start_at", startAt.toISOString());
        }
        if (!newParams.has("end_at") && process.EndAt.Valid) {
          const endAt = new Date(process.EndAt.Time);
          endAt.setMinutes(endAt.getMinutes() + 10);
          newParams.set("end_at", endAt.toISOString());
        }
        return newParams;
      });
    }
  }, [process]);

  return (
    <>
      <FullScreenLoadingIndicator loading={loading} />
      <div>
        {process?.isStarting ? (
          <div className="alert alert-warning text-center my-5">
            This process is being initiated.
            <button
              className="btn btn-link ms-3 p-0"
              type="button"
              title="Refresh page"
              onClick={() => setRefresh(new Date())}
            >
              <FontAwesomeIcon icon={faRefresh} />
            </button>
          </div>
        ) : (
          <div>
            <div className="d-flex justify-content-between align-items-start my-3">
              <div>
                <div className="d-flex align-items-start">
                  <ContactsIcon width={30} className="mt-2 me-3" />
                  <div className="">
                    <h1 className="fw-bold mb-1">{process?.GUID}</h1>
                    <span className="opacity-50">Process ID</span>
                  </div>
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <WhiteButton
                  as="button"
                  icon={StopIcon}
                  heading={process?.IsActive ? "Stop" : "Not active"}
                  subheading="Process"
                  onClick={() => stopProcess(process?.GUID)}
                  disabled={!process?.IsActive}
                />
                <WhiteButton
                  as="a"
                  title="Edit project in a new window"
                  to={`${baseURL}/workflows/builder/${project?.ID}`}
                  target={`project_${project?.ID}`}
                  icon={EditIcon}
                  heading="Edit"
                  subheading="Project"
                />
                <WhiteButton
                  as="RRLink"
                  title="Edit project in a new window"
                  target={`project_${project?.ID}`}
                  icon={ProjectIcon}
                  heading="Show"
                  subheading="Project processes"
                  to={{
                    pathname: `/reporting/processes/${project?.ID}`,
                    search: serachParams.toString(),
                  }}
                />
              </div>
            </div>
            <ReportingLinks />
            <div>
              {!!project && (
                <div className="my-4">
                  <ProjectInfo project={project} process={process} />
                </div>
              )}
            </div>
          </div>
        )}
        <hr className="text-secondary" />
        <div className="mt-4">
          <Timeline
            key={refresh}
            title={
              <>
                <span className="text-white"> Workflows</span>
                <button
                  type="button"
                  title="Show process logs"
                  className="btn btn-link ms-3 p-0 text-white"
                  onClick={() => setShowLogs({ GUID: process?.GUID })}
                >
                  <FontAwesomeIcon icon={faRectangleList} />
                </button>
              </>
            }
            loader={loader}
            processes={processes}
            total={total}
            // eslint-disable-next-line react/no-unstable-nested-components
            link={(p) =>
              p.Workflow ? (
                <td>
                  <div className="small text-secondary bg-transparent">
                    Workflow{" "}
                  </div>
                  <a
                    title="Show workflow in builder"
                    target="_blank"
                    href={`${baseURL}/workflows/builder/${project?.ID}/workflow/${p.Workflow.UUID}`}
                    rel="noreferrer"
                    className="bg-transparent"
                  >
                    {p.Workflow.Label}
                  </a>
                  <button
                    type="button"
                    title="Show workflow logs"
                    className="btn btn-link align-baseline p-0 ms-3 bg-transparent"
                    onClick={() =>
                      setShowLogs({ GUID: process?.GUID, runtimeID: p.ID })
                    }
                  >
                    <FontAwesomeIcon icon={faRectangleList} />
                  </button>
                </td>
              ) : (
                <td>
                  <div className="small text-secondary bg-transparent">
                    Project
                  </div>
                  <a
                    target="_blank"
                    href={`${baseURL}/workflows/builder/${project.ID}`}
                    rel="noreferrer"
                    className="bg-transparent"
                  >
                    {project.Label}
                  </a>
                </td>
              )
            }
          />
        </div>
      </div>
      <WorkflowLogs
        {...showLogs}
        handleClose={() => setShowLogs({ GUID: null, runtimeID: null })}
      />
    </>
  );
}
